import { FieldHelperProps, FieldInputProps, FieldMetaProps, useField } from 'formik';
import Select, { ValueType, components } from 'react-select';
import { MergedToAccountDetails } from '../../../pages/cryptoV2/models';
import { TextCurrencyIcon } from '../../currencyIcon/CurrencyIcon';
import { formatCurrency } from '../../../helpers/fiatFormatter';
import { useTranslation } from 'react-i18next';

type Props = {
    fieldname: string;
    accounts: MergedToAccountDetails[];
    currency: string;
    direction: 'From' | 'To';
};

export const AccountDropdown: React.FC<Props> = ({ fieldname, accounts, currency, direction }) => {
    const { t } = useTranslation('crypto');
    const [{ value }, , { setValue }] = useField(fieldname);

    const selectedAccount = accounts.find((acc) =>
        direction === 'From' ? acc.accountID === value : acc.genericId === value
    );

    const accountOptions = accounts.map((acc) => ({
        label: `${acc.label}${
            acc.balance !== null
                ? `(${formatCurrency(acc.balance ?? 0, currency, undefined, {
                      currencyDisplay: 'code',
                      maximumFractionDigits: 13,
                  })})`
                : ''
        }`,
        value: direction === 'From' ? acc.accountID : acc.genericId,
    }));

    const onChange = (
        opt: ValueType<
            {
                label: string;
                value: string | number | null;
            },
            false
        >
    ) => {
        setValue(opt?.value);
    };

    const SingleValue = ({ children, ...props }: any) => (
        <components.SingleValue {...props} className="AccountDropdown--value">
            <div className="From">
                <TextCurrencyIcon currencySymbol={selectedAccount?.currencySymbol ?? ''} />
                <div className="AccountInfo">
                    <p>{direction === 'From' ? t('from') : t('to')}</p>
                    <div className="AccountDropdownRow">
                        <h4>
                            {direction === 'To' ? (
                                <span className="notranslate" translate="no">
                                    {selectedAccount?.label}
                                </span>
                            ) : (
                                selectedAccount?.label
                            )}
                        </h4>
                        {selectedAccount?.balance !== null && (
                            <div className="Balance notranslate" translate="no">
                                {formatCurrency(
                                    selectedAccount?.balance ?? 0,
                                    currency,
                                    undefined,
                                    {
                                        currencyDisplay: 'code',
                                        maximumFractionDigits: 13,
                                    }
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </components.SingleValue>
    );

    const Option = (props: any) => (
        <components.Option {...props}>
            {direction === 'To' ? (
                <span className="notranslate" translate="no">
                    {props.data.label}
                </span>
            ) : (
                props.data.label
            )}
        </components.Option>
    );

    return (
        <Select
            className="AccountDropdown"
            classNamePrefix="AccountDropdown"
            value={accountOptions.find((opt) => opt.value === value)}
            onChange={onChange}
            options={accountOptions}
            isSearchable={false}
            components={{
                SingleValue,
                Option,
                IndicatorSeparator: null,
            }}
        />
    );
};
